import {
  imageResolutionForSettings,
  normalizeImageGenerationMetadata,
  preservesImageFormat,
  withOriginalImageFormat,
  type ImageGenerationSettings,
} from "../../functions/src/shared/imageGeneration";
/**
 * Frontend adapter for the unified backend AI generation Cloud Function.
 *
 * Replaces direct AI calls in geminiService.ts with httpsCallable calls
 * to the backend `generateImage` function.
 *
 * Images are passed as GCS storage paths (not base64).
 * Results include storagePath + downloadUrl (not base64 data URLs).
 */

import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { GeneratedContent, PromptEcho } from "../types";
import { listStudioProducts } from "@dataconnect/generated";
import { trackAiRequest } from "./analyticsService";
import {
  CLIENT_REQUEST_ID_KEY,
  GENERATE_IMAGE_TIMEOUT_MS,
  awaitRecoveredGeneration,
  isLostGenerationResponse,
  mayHaveReachedBackend,
  type GalleryImageRow,
} from "./recoveredGeneration";
import { AppError, mapFirebaseError } from "../utils/appError";
import { reportError } from "../utils/errorReporting";
import { mergeSerializedGenerationSettings } from "../utils/generationSettings";

// ---------------------------------------------------------------------------
// Types (mirror of backend types)
// ---------------------------------------------------------------------------

export type GenerateMode =
  // Image generation
  | "guided_staged"
  | "details"
  | "edit_annotation"
  | "recolor"
  | "rotation"
  | "cutout"
  | "background"
  | "transform_technical_drawing"
  | "photo_to_sketch"
  | "free_prompt"
  | "highlights"
  | "multi_product_scene"
  | "aspect_ratio_conversion"
  | "inpaint_style_transfer"
  | "add_avatar"
  | "lifestyle_scene"
  | "highlight_generate"
  // Analysis
  | "analyze_measurement_headers"
  | "analyze_dimensions"
  | "detect_product_area"
  | "analyze_product"
  | "analyze_product_staging"
  | "analyze_avatar_quality"
  | "analyze_color_swatch"
  | "analyze_material_reference"
  | "analyze_brand"
  | "analyze_brand_style_reference"
  | "generate_vibes"
  | "generate_scenes"
  | "analyze_scene_image"
  | "analyze_custom_room_style"
  | "analyze_lifestyle_reference"
  | "analyze_highlights_reference"
  | "analyze_video_scene"
  | "improve_custom_context"
  | "improve_inpaint_scene_context"
  | "highlight_plan"
  | "highlight_propose";

interface GenerateImageRequest {
  mode: GenerateMode;
  images: Record<string, string | string[] | undefined>;
  params: Record<string, unknown>;
}

interface GenerateImageResponse {
  generationSettings?: GeneratedContent["generationSettings"];
  id: string;
  storagePath: string | null;
  downloadUrl: string | null;
  text: string | null;
  /** Database UUID assigned by createStudioImage on the backend */
  dbId?: string | null;
  /** Dev/staging-only prompt echo. Typed loosely here because the backend
   * is the authority on its shape; narrowed at the boundary below. */
  debugRequest?: PromptEcho;
  provider?: string | null;
  model?: string | null;
  providerDurationMs?: number | null;
  attemptsTried?: number | null;
  creditCost?: number | null;
  requestType?: string | null;
}

// ---------------------------------------------------------------------------
// Main call function
// ---------------------------------------------------------------------------

const generateImageFn = httpsCallable<
  GenerateImageRequest,
  GenerateImageResponse
>(functions, "generateImage", { timeout: 570_000 });

/** Flattens the project's gallery into the rows the recovery compares. */
async function listGalleryRows(projectId: string): Promise<GalleryImageRow[]> {
  const response = await listStudioProducts({ projectId });
  return response.data.studioProducts.flatMap((product) =>
    (product.generatedContent ?? []).flatMap((image) =>
      // A row without a storage path cannot be handed back as a result, so it
      // is not a candidate however recent it is.
      image.imageUrl
        ? [
            {
              id: image.id,
              productId: product.id,
              imageUrl: image.imageUrl,
              generationSettings: image.generationSettings ?? null,
              createdAt: String(image.createdAt),
            },
          ]
        : []
    )
  );
}

/**
 * Calls the backend generateImage Cloud Function.
 * Returns a GeneratedContent compatible with the existing UI.
 */
/**
 * Set the current project/product context so that generated images
 * are stored under the correct GCS path (f/{uid}/{projectId}/{productId}/generated/).
 * Call this before triggering generation from a route that knows the context.
 */
let _projectContext: {
  projectId?: string;
  productId?: string;
  imageType?: string;
  generationSettings?: string;
} = {};

/** Capture before any upload/await so concurrent jobs cannot replace the selection. */
export function snapshotGenerationContext(): Readonly<typeof _projectContext> {
  return { ..._projectContext };
}

export function setGenerationContext(
  projectId?: string,
  productId?: string,
  imageType?: string,
  generationSettings?: string
): void {
  _projectContext = { projectId, productId, imageType, generationSettings };
}

async function requireCallableAuth(): Promise<void> {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) {
    throw new AppError(
      "auth/not_authenticated",
      "You must be signed in before starting generation."
    );
  }

  await user.getIdToken();
}

/**
 * Merge extra fields into the current generation context's serialized
 * `generationSettings`, leaving project/product/imageType untouched.
 *
 * Call this when metadata only becomes available after `setGenerationContext`
 * but before the generation call that persists the record — e.g. an
 * AI-resolved style label/prompt. Without it the persisted backend record (the
 * source of truth after reload) would be missing fields the in-memory result
 * shows during the live session.
 */
export function mergeGenerationContextSettings(
  extra: Record<string, unknown>
): void {
  _projectContext = {
    ..._projectContext,
    generationSettings: mergeSerializedGenerationSettings(
      _projectContext.generationSettings,
      extra
    ),
  };
}

/**
 * Flatten the `images` dict (values can be `string | string[] | undefined`)
 * into a de-duplicated list of storage paths, for analytics. Only includes
 * strings that look like Firebase Storage paths (not base64).
 */
function collectSourceImagePaths(
  images: Record<string, string | string[] | undefined>
): string[] {
  const out = new Set<string>();
  for (const value of Object.values(images)) {
    if (!value) continue;
    const arr = Array.isArray(value) ? value : [value];
    for (const v of arr) {
      if (typeof v === "string" && v.length > 0 && !v.startsWith("data:")) {
        out.add(v);
      }
    }
  }
  return [...out];
}

function buildProviderAnalytics(data?: GenerateImageResponse): {
  provider: string;
  model?: string;
  providerDurationMs?: number;
  attemptsTried?: number;
  creditCost?: number;
  requestType?: string;
} {
  return {
    provider: data?.provider ?? "backend_provider_chain",
    model: data?.model ?? undefined,
    providerDurationMs: data?.providerDurationMs ?? undefined,
    attemptsTried: data?.attemptsTried ?? undefined,
    creditCost: data?.creditCost ?? undefined,
    requestType: data?.requestType ?? undefined,
  };
}

function buildRequestAnalytics(params: Record<string, unknown>): {
  requestedModel?: string;
  resolution?: string;
  aspectRatio?: string;
} {
  const selected = params.imageGeneration as
    ImageGenerationSettings | undefined;
  const requestedModel =
    selected?.model ??
    (typeof params.model === "string" ? params.model : undefined);
  const resolution = selected
    ? imageResolutionForSettings(selected)
    : typeof params.imageSize === "string"
      ? params.imageSize
      : typeof params.resolution === "string"
        ? params.resolution
        : undefined;
  const aspectRatio = selected
    ? selected.customSize
      ? `${selected.customSize.width}:${selected.customSize.height}`
      : selected.aspectRatio || "original"
    : typeof params.aspectRatio === "string"
      ? params.aspectRatio
      : typeof params.targetAspectRatio === "string"
        ? params.targetAspectRatio
        : undefined;

  return {
    requestedModel,
    resolution,
    aspectRatio,
  };
}

/**
 * Swaps base64 payloads for a size marker before a request is put in the debug
 * report. The report is a file someone opens in a browser; a few megabytes of
 * inlined mask bytes would make it unreadable and tell them nothing.
 */
function redactPayloads(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) =>
      typeof value === "string" && /base64$/i.test(key)
        ? [key, `<${value.length} base64 chars>`]
        : [key, value]
    )
  );
}

export async function callGenerateImage(
  mode: GenerateMode,
  images:
    | Record<string, string | string[] | undefined>
    | (() => Promise<Record<string, string | string[] | undefined>>),
  params: Record<string, unknown>,
  capturedContext?: ReturnType<typeof snapshotGenerationContext>
): Promise<GeneratedContent> {
  const startMs = Date.now();
  // Snapshot the mutable context so concurrent generations don't corrupt
  // the analytics attribution for this in-flight request.
  const contextSnapshot = capturedContext || snapshotGenerationContext();
  // Resolved inside the try below so that callers passing a thunk (which may
  // upload fresh inputs to GCS first) still emit an `ai_request` failure event
  // if that pre-call resolution throws.
  let sourceImagePaths: string[] = [];
  let savedSettings: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(contextSnapshot.generationSettings || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed))
      savedSettings = parsed;
  } catch {
    /* legacy metadata */
  }
  const requestedSettings = (params.imageGeneration ??
    savedSettings.imageGeneration) as ImageGenerationSettings | undefined;
  const selectedImageGeneration =
    requestedSettings && preservesImageFormat(mode)
      ? withOriginalImageFormat(requestedSettings)
      : requestedSettings;
  // Firebase callable encoding turns explicit undefined fields into null.
  // Omit unset options so the backend does not read a cleared custom format
  // as malformed pixel dimensions (also applies to model/format switches).
  const imageGeneration = selectedImageGeneration
    ? (Object.fromEntries(
        Object.entries(selectedImageGeneration).filter(
          ([, value]) => value !== undefined
        )
      ) as ImageGenerationSettings)
    : undefined;
  // Stamped on every call, not just the ones that go wrong, because the row
  // has to carry it before anyone knows the answer will be lost. The backend
  // keeps settings keys it does not recognise, so this reaches the stored
  // StudioImage and lets a recovery prove a row is *this* request's result.
  const requestId = crypto.randomUUID();
  const enrichedParams = {
    ...params,
    ...contextSnapshot,
    ...(imageGeneration ? { imageGeneration } : {}),
    generationSettings: mergeSerializedGenerationSettings(
      contextSnapshot.generationSettings,
      { [CLIENT_REQUEST_ID_KEY]: requestId }
    ),
  };
  // A caller may also pass the optional settings object itself as undefined.
  // Keep explicit null/invalid values intact for backend validation.
  if (enrichedParams.imageGeneration === undefined) {
    delete enrichedParams.imageGeneration;
  }

  // When the backend's own clock started. Auth and reference uploads happen
  // first and can take a while, and none of that counts against the function's
  // timeout, so a recovery measured from startMs would give up early by
  // exactly the time spent getting ready.
  let requestSentMs = startMs;

  try {
    await requireCallableAuth();

    const resolvedImages =
      typeof images === "function" ? await images() : images;
    sourceImagePaths = collectSourceImagePaths(resolvedImages);

    requestSentMs = Date.now();
    const { data } = await generateImageFn({
      mode,
      images: resolvedImages,
      params: enrichedParams,
    });
    // Captured here rather than rebuilt by the caller: the caller cannot see
    // the context this function injects, nor that it set `debugRequest`, so a
    // reconstruction is always a near-miss of the request it claims to show.
    const sentRequest =
      params.debugRequest === true
        ? {
            mode,
            images: resolvedImages,
            params: redactPayloads(enrichedParams),
          }
        : undefined;

    // Resolve a display URL from the storage path.
    // First try the backend-provided downloadUrl, then fall back to the
    // frontend's getDownloadURL which handles emulator vs production.
    let thumbnailUrl: string | undefined;
    if (data.downloadUrl) {
      thumbnailUrl = data.downloadUrl;
    }
    if (!thumbnailUrl && data.storagePath) {
      const { resolveDownloadUrl } = await import("../utils/imageUtils");
      const url = await resolveDownloadUrl(data.storagePath);
      if (url) {
        thumbnailUrl = url;
      }
    }

    trackAiRequest({
      mode,
      ...buildProviderAnalytics(data),
      ...buildRequestAnalytics(enrichedParams),
      success: true,
      durationMs: Date.now() - startMs,
      projectId: contextSnapshot.projectId,
      productId: contextSnapshot.productId,
      imageType: contextSnapshot.imageType,
      sourceImagePaths,
    });

    return {
      id: data.dbId ?? data.id,
      image: thumbnailUrl ?? null,
      text: data.text,
      video: null,
      storagePath: data.storagePath ?? undefined,
      thumbnailUrl,
      fullUrl: thumbnailUrl, // Backend returns full-res; thumbnail created async
      dbPersisted: !!data.dbId,
      generationSettings:
        data.generationSettings ??
        normalizeImageGenerationMetadata(
          savedSettings,
          data.model ?? (savedSettings.model as string | undefined),
          imageGeneration
        ),
      ...(data.debugRequest ? { debugRequest: data.debugRequest } : {}),
      ...(sentRequest ? { debugCallable: sentRequest } : {}),
    };
  } catch (error) {
    // A failure without an answer does not mean the generation failed. The
    // backend writes the image, saves the row and charges the credit before
    // it replies, and nothing cancels it once the browser has gone away. So
    // rather than reporting a failure the user has already paid for, wait for
    // the row the backend is still writing and hand it back as the result.
    // The caller cannot tell the difference, so every call site keeps working
    // unchanged: it sees a generation that simply took longer.
    const { projectId, productId } = contextSnapshot;
    const recovered =
      projectId &&
      productId &&
      isLostGenerationResponse(error) &&
      // Offline fails the fetch the same way, but nothing was sent, so there
      // is no image on its way and nothing to wait for.
      mayHaveReachedBackend()
        ? await awaitRecoveredGeneration({
            projectId,
            requestId,
            // Only as long as the backend itself could still be running.
            deadlineMs: Math.max(
              0,
              GENERATE_IMAGE_TIMEOUT_MS - (Date.now() - requestSentMs)
            ),
            listImages: listGalleryRows,
          }).catch((recoveryError: unknown) => {
            // The recovery is the thing that decides whether the user is told
            // their image failed, so it breaking must not look like a plain
            // miss. Falling through still reports the result as unconfirmed.
            reportError(recoveryError, {
              context: "callGenerateImage.recovery",
              mode,
              projectId,
              productId,
            });
            return null;
          })
        : null;

    if (recovered) {
      const { resolveDownloadUrl } = await import("../utils/imageUtils");
      const url = (await resolveDownloadUrl(recovered.imageUrl)) ?? undefined;
      trackAiRequest({
        mode,
        provider: "backend_provider_chain",
        ...buildRequestAnalytics(enrichedParams),
        success: true,
        // Separates a plain success from one the browser nearly lost, which
        // is the only measure of how often the connection breaks mid-request.
        recovered: true,
        durationMs: Date.now() - startMs,
        projectId,
        productId,
        imageType: contextSnapshot.imageType,
        sourceImagePaths,
      });
      return {
        id: recovered.id,
        image: url ?? null,
        text: null,
        video: null,
        storagePath: recovered.imageUrl,
        thumbnailUrl: url,
        fullUrl: url,
        // The backend already stored it, so the caller must not upload or
        // persist it a second time.
        dbPersisted: true,
        generationSettings:
          parseGenerationSettings(recovered.generationSettings) ??
          normalizeImageGenerationMetadata(
            savedSettings,
            savedSettings.model as string | undefined,
            imageGeneration
          ),
      };
    }

    trackAiRequest({
      mode,
      provider: "backend_provider_chain",
      ...buildRequestAnalytics(enrichedParams),
      success: false,
      durationMs: Date.now() - startMs,
      errorMessage: error instanceof Error ? error.message : String(error),
      projectId,
      productId,
      imageType: contextSnapshot.imageType,
      sourceImagePaths,
    });
    // We waited out the backend's own budget and no image arrived. It may
    // still land, so the message says to check the gallery rather than to
    // generate again, which would charge for a second copy.
    if (projectId && productId && isLostGenerationResponse(error)) {
      throw new AppError(
        "generation/result_pending",
        error instanceof Error ? error.message : String(error),
        error
      );
    }
    throw mapFirebaseError(error);
  }
}

/**
 * Reads the settings a recovered row stored at generation time.
 *
 * @param {string | null} raw Serialized settings from the gallery row.
 * @return {Record<string, unknown> | null} Parsed settings, or null.
 */
function parseGenerationSettings(
  raw: string | null
): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** What an analysis call returns beyond the parsed JSON, for debug reports. */
export interface AnalysisDiagnostics {
  /** The model's response text before parsing — what the sanitizer saw. */
  rawText: string;
  /** Dev/staging-only echo of the prompt the backend assembled. */
  debugRequest?: PromptEcho;
  /** The callable request as it actually went out (only when requested). */
  debugCallable?: {
    mode: string;
    images: Record<string, string | string[] | undefined>;
    params: Record<string, unknown>;
  };
  provider?: string;
  model?: string;
  providerDurationMs?: number;
  attemptsTried?: number;
}

/**
 * Calls the backend for analysis modes that return text/JSON and keeps the
 * diagnostics the backend sends alongside. `callAnalysis` below is the
 * everyday shape; this one exists for the debug reports, which need the raw
 * text and the prompt echo that a parsed result throws away.
 */
export async function callAnalysisWithDiagnostics<T>(
  mode: GenerateMode,
  images: Record<string, string | string[] | undefined>,
  params: Record<string, unknown>
): Promise<AnalysisDiagnostics & { data: T }> {
  const startMs = Date.now();
  const sourceImagePaths = collectSourceImagePaths(images);
  const contextSnapshot = { ..._projectContext };

  try {
    await requireCallableAuth();

    const { data } = await generateImageFn({ mode, images, params });
    // Same reasoning as in callGenerateImage: the caller cannot rebuild the
    // request it did not assemble, so it is captured where it is sent.
    const sentRequest =
      params.debugRequest === true
        ? { mode, images, params: redactPayloads(params) }
        : undefined;

    if (!data.text) {
      throw new AppError(
        "generation/all_providers_failed",
        `Analysis mode ${mode} returned no text response.`
      );
    }

    const parsed = JSON.parse(data.text) as T;

    trackAiRequest({
      mode,
      ...buildProviderAnalytics(data),
      ...buildRequestAnalytics(params),
      success: true,
      durationMs: Date.now() - startMs,
      projectId: contextSnapshot.projectId,
      productId: contextSnapshot.productId,
      imageType: contextSnapshot.imageType,
      sourceImagePaths,
    });

    return {
      data: parsed,
      rawText: data.text,
      ...(data.debugRequest ? { debugRequest: data.debugRequest } : {}),
      ...(sentRequest ? { debugCallable: sentRequest } : {}),
      provider: data.provider ?? undefined,
      model: data.model ?? undefined,
      providerDurationMs: data.providerDurationMs ?? undefined,
      attemptsTried: data.attemptsTried ?? undefined,
    };
  } catch (error) {
    trackAiRequest({
      mode,
      provider: "backend_provider_chain",
      ...buildRequestAnalytics(params),
      success: false,
      durationMs: Date.now() - startMs,
      errorMessage: error instanceof Error ? error.message : String(error),
      projectId: contextSnapshot.projectId,
      productId: contextSnapshot.productId,
      imageType: contextSnapshot.imageType,
      sourceImagePaths,
    });
    throw mapFirebaseError(error);
  }
}

/**
 * Calls the backend for analysis modes that return text/JSON.
 * Returns the parsed text response.
 */
export async function callAnalysis<T>(
  mode: GenerateMode,
  images: Record<string, string | string[] | undefined>,
  params: Record<string, unknown>
): Promise<T> {
  const { data } = await callAnalysisWithDiagnostics<T>(mode, images, params);
  return data;
}

/**
 * Calls the backend for text generation modes (improve context, etc.).
 * Returns the raw text response.
 */
export async function callTextGeneration(
  mode: GenerateMode,
  params: Record<string, unknown>
): Promise<string> {
  const startMs = Date.now();
  const contextSnapshot = { ..._projectContext };

  try {
    await requireCallableAuth();

    const { data } = await generateImageFn({ mode, images: {}, params });

    if (!data.text) {
      throw new AppError(
        "generation/all_providers_failed",
        `Text generation mode ${mode} returned no text.`
      );
    }

    trackAiRequest({
      mode,
      ...buildProviderAnalytics(data),
      ...buildRequestAnalytics(params),
      success: true,
      durationMs: Date.now() - startMs,
      projectId: contextSnapshot.projectId,
      productId: contextSnapshot.productId,
      imageType: contextSnapshot.imageType,
    });

    return data.text;
  } catch (error) {
    trackAiRequest({
      mode,
      provider: "backend_provider_chain",
      ...buildRequestAnalytics(params),
      success: false,
      durationMs: Date.now() - startMs,
      errorMessage: error instanceof Error ? error.message : String(error),
      projectId: contextSnapshot.projectId,
      productId: contextSnapshot.productId,
      imageType: contextSnapshot.imageType,
    });
    throw mapFirebaseError(error);
  }
}
